import { getAuth } from 'firebase/auth';
import {
  deleteObject,
  getBlob,
  getStorage,
  ref,
  uploadBytes,
} from 'firebase/storage';
import { useCallback, useEffect, useState } from 'react';

export function useFirebaseProfileImage() {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [bgImageFirebaseUrl, setBgImageFirebaseUrl] = useState<string | null>(
    null,
  );

  const storage = getStorage();
  const auth = getAuth();

  const uploadBgImageFirebase = useCallback(
    async (file: File) => {
      try {
        setIsLoading(true);
        const user = auth.currentUser;
        if (user) {
          const storagePath = `images/users/${user.uid}/background-image.jpg`;
          // Upload file to the user’s path (overwrites same file)
          const backgroundImageRef = ref(storage, storagePath);
          await uploadBytes(backgroundImageRef, file);
          // Retrieve the image blob securely
          const blob = await getBlob(backgroundImageRef);
          const localUrl = URL.createObjectURL(blob);
          setBgImageFirebaseUrl(localUrl);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [auth, storage],
  );

  const fetchBgImageFirebase = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const user = auth.currentUser;
      if (user) {
        const storagePath = `images/users/${user.uid}/background-image.jpg`;
        const fileRef = ref(storage, storagePath);
        const blob = await getBlob(fileRef);
        const localUrl = URL.createObjectURL(blob);
        setBgImageFirebaseUrl(localUrl);
        return localUrl;
      }
    } catch (_e) {
      setBgImageFirebaseUrl(null);
      return null;
    } finally {
      setIsLoading(false);
    }
    return null;
  }, [auth, storage]);

  const removeBackgroundImageFirebase = useCallback(async () => {
    try {
      setIsLoading(true);
      const user = auth.currentUser;
      if (user) {
        const storagePath = `images/users/${user.uid}/background-image.jpg`;
        const backgroundImageRef = ref(storage, storagePath);

        await deleteObject(backgroundImageRef);
        setBgImageFirebaseUrl(null);
      }
    } finally {
      setIsLoading(false);
    }
  }, [auth, storage]);

  useEffect(() => {
    fetchBgImageFirebase().then();
    return () => {
      if (bgImageFirebaseUrl) URL.revokeObjectURL(bgImageFirebaseUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchBgImageFirebase]);

  return {
    bgImageFirebaseUrl,
    uploadBgImageFirebase,
    removeBackgroundImageFirebase,
    isLoading,
    error,
  };
}
